import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import {
  Environment,
  EnvironmentTree,
} from "src/components/shared-components/class/general-classes/environment";
import { GenericIDs } from "src/model/class/general classes/generic-ids";
import { User } from "src/model/class/general classes/user";
import { UserEnvironment } from "src/model/class/general classes/userEnvironment";
import { AccessResult } from "src/components/shared-components/enum/enumAccessControl";
import { CardKind } from "src/components/shared-components/enum/enumCard";
import {
  EnvironmentCategory,
  EnvironmentType,
} from "src/components/shared-components/enum/enumEnvironment";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import {
  ApplicationTheme,
  ManufacturerSystem,
} from "src/components/shared-components/enum/enumMisc";
import { EnvironmentAccessControlMaintanance } from "src/model/interface/environment-access-control-maintanance";

import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { EventService } from "src/service/event.service";
import {
  environmentAccessUserOpenableLoadListFromApiAction,
  environmentDeviceLoadInitStartAction,
  environmentLoadInitStartAction,
} from "src/state/actions/environment.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import {
  listEnvironment,
  listEnvironmentAccessControlMaintanance,
  listEnvironmentAccessUserOpenable,
  listEnvironmentDevices,
} from "src/state/selectors/environment.selectors";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { AppSettings } from "../components/shared-components/class/general-classes/app-settings";
import { GenericApiData } from "../components/shared-components/class/general-classes/generic-api-data";
import { AlertService } from "./alert.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { ThemeService } from "./theme.service";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { CardProgrammerKind } from "src/components/shared-components/enum/enumCardProgrammerKind";
import { PermissionService } from "./permission.service";
import { ItemEnvironmentCategory } from "src/model/class/general classes/item-environment-category";
import { PopupEnvironmentCategoryComponent } from "src/components/popup/popup-environment-category/popup-environment-category.component";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { ModalController } from "@ionic/angular";
import { EnvironmentCategoryReverse } from "src/model/interface/environment-category-reverse";

import { LoaderService } from "./loader.service";

@Injectable({
  providedIn: "root",
})
export class EnvironmentService implements OnDestroy, iEvent, iTranslation {
  private _logContext: string = "Environment.service";

  private _selectCurrentUserSub: Subscription = null;
  private _translateSubscription: Subscription = null;
  private _currentUser: CurrentSessionData = null;
  private _isUserAuthenticated: boolean = false;
  private _lblNewApartament: string = "";
  private _lblNewCommonArea: string = "";
  private _lblNewElevatorFloor: string = "";
  private _lblNewExternalAccess: string = "";
  private _lblNewFloor: string = "";
  private _lblNewGarage: string = "";
  private _lblSelectBuildingPartsToCreate: string = "";

  public editEnvironment: BehaviorSubject<{ [key: string]: number }> =
    new BehaviorSubject({ category: 0, idParent: -1 });

  private _expandedEnvironments: Set<number> = new Set<number>();
  private _currentBuildingId: number = null;
  private _selectedBuildingSubscription: Subscription = null;

  private _accessControlManufacturerNoRangeForCardNumber: Array<ManufacturerSystem> =
    [
      ManufacturerSystem.GENERIC_KEYPAD,
      ManufacturerSystem.BLUMOTIX_10,
      ManufacturerSystem.BIG_VIRTUAL,
      ManufacturerSystem.BIG_VIRTUAL_KEYPAD,
      ManufacturerSystem.MIKROTIK,
      ManufacturerSystem.DAHUA,
      ManufacturerSystem.DAHUA_CARD_READER,
    ];

  private _compatibleAccessManufacturerSystem: Array<ManufacturerSystem> = [
    ManufacturerSystem.VIMAR_EELECTRON,
    ManufacturerSystem.SCHNEIDER,
    ManufacturerSystem.SIEMENS,
    ManufacturerSystem.EELECTRON,
    ManufacturerSystem.GEWISS,
  ];

  private _compatibleAccessManufacturerSystemMifare: Array<ManufacturerSystem> =
    [
      ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS,
      ManufacturerSystem.DAHUA_CARD_READER,
      ManufacturerSystem.BIG_VIRTUAL_MIFARE,
    ];

  private _compatibleAccessManufacturerSystemKeypad: Array<ManufacturerSystem> =
    [
      ManufacturerSystem.DAHUA,
      ManufacturerSystem.BLUMOTIX_10,
      ManufacturerSystem.BIG_VIRTUAL_KEYPAD,
      ManufacturerSystem.GENERIC_KEYPAD,
      ManufacturerSystem.EELECTRON_KEYPAD,
    ];

  private _compatibleAccessManufacturerSystemCardForKeypad: Array<ManufacturerSystem> =
    [
      ManufacturerSystem.VIMAR_EELECTRON,
      ManufacturerSystem.SCHNEIDER,
      ManufacturerSystem.SIEMENS,
      ManufacturerSystem.EELECTRON,
      ManufacturerSystem.GEWISS,
      ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS,
      ManufacturerSystem.ABB_MAC2,
      ManufacturerSystem.ABB_MILLENIUM,
      ManufacturerSystem.ABB_TACTEO,
      ManufacturerSystem.BIG_VIRTUAL_MIFARE,
    ];

  private _listManufacturerSystem: Array<ManufacturerSystem> =
    new Array<ManufacturerSystem>();

  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _mqttService: MqttClientService,
    private _loggerService: LoggerService,
    private _eventService: EventService,
    private _alertService: AlertService,
    private _themeService: ThemeService,
    private _translateService: TranslateService,
    private _permissionService: PermissionService,
    private _modalController: ModalController,
    private _loaderService: LoaderService
  ) {
    this.loadCurrentUser();

    this.subscribeEvents();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeBuildingChange();
  }

  /**
   * Load translation
   */
  public loadTranslation() {
    this._translateService
      .get([
        "dictionary.newApartament",
        "dictionary.newCommonArea",
        "dictionary.newElevatorFloor",
        "dictionary.newExternaleAccess",
        "dictionary.newFloor",
        "dictionary.newGarage",
        "dictionary.selectBuildingPartCreate",
      ])
      .subscribe((translation) => {
        this._lblNewApartament = translation["dictionary.newApartament"];
        this._lblNewCommonArea = translation["dictionary.newCommonArea"];
        this._lblNewElevatorFloor = translation["dictionary.newElevatorFloor"];
        this._lblNewExternalAccess =
          translation["dictionary.newExternaleAccess"];
        this._lblNewFloor = translation["dictionary.newFloor"];
        this._lblNewGarage = translation["dictionary.newGarage"];
        this._lblSelectBuildingPartsToCreate =
          translation["dictionary.selectBuildingPartCreate"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }

  /** Destroy event service */
  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    this.unsubscribeEvents();
    this._translateSubscription?.unsubscribe();
  }

  /**
   *  Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  public Init(): void {
    this._store.dispatch(environmentLoadInitStartAction());

    this._store.dispatch(environmentDeviceLoadInitStartAction());
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadEnvironmentFullListData(): void {
    this.Init();
  }

  /**
   * Get environment data from API. If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  public getEnvironments(
    idArea?: number,
    idEnvironment?: number,
    extraData = true,
    infoSmartComponent = false,
    childAreaLevel = 0
  ): Observable<GenericApiData> {
    let queryString: string = "";
    if (idArea) {
      queryString += "?idArea=" + idArea;
    }
    if (idEnvironment) {
      queryString += "?id=" + idEnvironment;
    }

    if (queryString) {
      queryString += "&";
    } else {
      queryString += "?";
    }

    queryString += "extraData=" + extraData;
    queryString += "&infoSmartComponent=" + infoSmartComponent;
    queryString += "&childAreaLevel=" + childAreaLevel;

    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_ENVIRONMENT + queryString
    );
  }

  /**
   * Sort environments recursively
   * @param environments
   * @returns
   */
  public sortEnvironmentsRecursively(
    environments: EnvironmentTree[]
  ): EnvironmentTree[] {
    if (!environments || environments.length === 0) {
      return [];
    }

    return environments
      .sort((a, b) =>
        a.descriptionEnvironment.localeCompare(
          b.descriptionEnvironment,
          undefined,
          { numeric: true, sensitivity: "base" }
        )
      )
      .map((env) => ({
        ...env,
        children: this.sortEnvironmentsRecursively(env.children),
      }));
  }

  /**
   * Launch open door command by environment id
   */
  public launchOpenDoorCommand(idEnvironment: number): Promise<Object> {
    return this._http
      .get(
        environment.httpServerRoot +
        "/api/environment/requestaccess/" +
        idEnvironment +
        "/direct"
      )
      .toPromise();
  }
  /**
   * Get environment data from API with last modify data store.
   * If api call fallback in error costructor, it propagate an Observable notification with .error() function
   * If original data store isn't changed, api return nothin (code 200)
   *
   * @param environmentStore Environment store data
   * @returns
   */
  public getEnvironmentByLastModify(
    environmentStore: GenericApiData
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string =
      environmentStore?.lastAPIModified || "";
    const queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_ENVIRONMENT +
        queryString
      )
      .pipe(
        tap((environments) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_http.get - " + AppSettings.API_URL_ENVIRONMENT,
            "GetEnvironmentByLastModify()",
            "environments: " + JSON.stringify(environments)
          );
        }),
        map((returnEnvironmentStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnEnvironmentStore) {
            environmentStore = returnEnvironmentStore;
          }

          return environmentStore;
        })
      );
  }

  /**
   * Get environment for access control
   *
   * @returns
   */
  public getEnvironmentForAccessControl(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot +
      AppSettings.API_URL_ENVIRONMENT +
      "/accesscontrol"
    );
  }

  /**
   * Get environment that the current user can open by BIGStudio3 from API.
   *  If api call fallback in error costructor,it set the appropriate flag "_isErrorInAccessesOpenableByUserAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   */
  public getAccessesToOpenByUser(): Observable<GenericApiData> {
    let returnApiObject: GenericApiData = null;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_ENVIRONMENT +
        "/accessesforuser"
      )
      .pipe(
        tap(
          (returnEnvirnomentStore: GenericApiData) =>
            (returnApiObject = returnEnvirnomentStore)
        ),
        map((returnEnvirnomentStore: GenericApiData) => {
          return (returnEnvirnomentStore.data || []) as Array<Environment>;
        }),
        map((listEnvironment: Array<Environment>) => {
          return listEnvironment.filter(
            (environment: Environment) =>
              environment.environmentType == EnvironmentType.COMMON_AREA ||
              environment.environmentType == EnvironmentType.ROOM ||
              environment.environmentType == EnvironmentType.SUITE ||
              environment.environmentType == EnvironmentType.CUSTOMIZED
          );
        }),
        map((listEnvironment: Array<Environment>) => {
          return listEnvironment.filter(
            (environment: Environment) => environment.openDoorCommand !== ""
          );
        }),
        tap(
          (listEnvironment: Array<Environment>) =>
            (returnApiObject.data = listEnvironment)
        ),
        map((listEnvironment: Array<Environment>) => returnApiObject)
      );
  }

  /**
   * Execute open door command request by id environment
   * check also the result to give a feedback to the user
   *
   * @param idEnvironment id Environment to open
   */
  public openDoorEnvironmentRequestCommand(
    idEnvironment: number
  ): Promise<AccessResult> {
    const mqttAddressEnvironmentOpenDoor =
      this.getEnvironnmentRequestCommandAddress(idEnvironment);
    const mqttMessage = this._currentUser.id.toString();

    this._mqttService.publishAddress(
      mqttAddressEnvironmentOpenDoor,
      mqttMessage
    );

    return this.checkOpenDoorEnvironmentRequestCommandFeedBack(
      idEnvironment,
      this._currentUser.id
    );
  }

  /**
   * Group initial list environment by environment type and category.
   * It return object that own key are environmentType|category.
   * Each key is array of specific environment with that type and category
   *
   * @param listEnvironment List environment  to grouyp
   * @returns
   */
  public groupEnvironmentByTypeAndCategory(
    listEnvironment: Array<Environment>
  ): object {
    let returnObject = null;

    if (listEnvironment.length) {
      returnObject = {};

      listEnvironment.map((currentEnvironment: Environment) => {
        const keyCategory =
          this.normalizeKeyEnvironmentCategory(currentEnvironment);

        if (!returnObject[keyCategory]) {
          returnObject[keyCategory] = new Array<any>();
        }

        returnObject[keyCategory].push(currentEnvironment);

        return currentEnvironment;
      });
    }

    return returnObject;
  }

  /**
   * Check if specific environment is for access control.
   * If environment is CISA (zoner or category or rooms), by default it's always access control environment
   */
  public isEnvironmentForAccessControl(
    enviroment: EnvironmentAccessControlMaintanance
  ): boolean {
    return (
      !(
        enviroment.manufacturerSystem === ManufacturerSystem.AMBIGOUS ||
        enviroment.manufacturerSystem === ManufacturerSystem.UNKNOWN ||
        enviroment.manufacturerSystem === ManufacturerSystem.NONE
      ) ||
      enviroment.environmentType === EnvironmentType.CISA_CATEGORY ||
      enviroment.environmentType === EnvironmentType.CISA_ZONE
    );
  }

  //#region
  /**
   * Returns true if the environment is compatible with paper
   *
   * @param manufacturerSystem1 Manufacturer system to check
   * @param cardKind Card kind to compare manufacturer system
   * @returns
   */
  public manufacturerSystemsCardKindCompatible(
    manufacturerSystem1: ManufacturerSystem,
    cardKind: CardKind
  ): boolean {
    let returnValue: boolean = true;

    if (returnValue) {
      switch (cardKind) {
        case CardKind.REAL_CARD:
          returnValue =
            manufacturerSystem1 == ManufacturerSystem.ABB ||
            manufacturerSystem1 == ManufacturerSystem.ABB_MAC2 ||
            manufacturerSystem1 == ManufacturerSystem.ABB_MILLENIUM ||
            manufacturerSystem1 == ManufacturerSystem.ABB_TACTEO ||
            manufacturerSystem1 == ManufacturerSystem.BLUMOTIX_10 ||
            manufacturerSystem1 == ManufacturerSystem.BLUMOTIX_5 ||
            manufacturerSystem1 == ManufacturerSystem.CISA ||
            manufacturerSystem1 == ManufacturerSystem.DAHUA ||
            manufacturerSystem1 == ManufacturerSystem.DAHUA_CARD_READER ||
            manufacturerSystem1 ==
            ManufacturerSystem.DORMAKABASAFLOKSYSTEM6000 ||
            manufacturerSystem1 == ManufacturerSystem.EELECTRON ||
            manufacturerSystem1 == ManufacturerSystem.ELECTRONE ||
            manufacturerSystem1 == ManufacturerSystem.GEWISS ||
            manufacturerSystem1 == ManufacturerSystem.SALTO_IS ||
            manufacturerSystem1 == ManufacturerSystem.SCHNEIDER ||
            manufacturerSystem1 == ManufacturerSystem.SIEMENS ||
            manufacturerSystem1 == ManufacturerSystem.VIMAR_EELECTRON ||
            manufacturerSystem1 ==
            ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS ||
            manufacturerSystem1 == ManufacturerSystem.WEIGAND ||
            manufacturerSystem1 == ManufacturerSystem.BIG_VIRTUAL_KEYPAD ||
            manufacturerSystem1 == ManufacturerSystem.SALTO_KS ||
            manufacturerSystem1 == ManufacturerSystem.GENERIC_KEYPAD ||
            manufacturerSystem1 == ManufacturerSystem.EELECTRON_KEYPAD ||
            manufacturerSystem1 == ManufacturerSystem.EELECTRON_MIFARE ||
            manufacturerSystem1 == ManufacturerSystem.BIG_VIRTUAL_MIFARE;
          break;
        case CardKind.PHONE_NUMBER:
          returnValue = true;
          break;
        case CardKind.PLATE:
          returnValue =
            manufacturerSystem1 == ManufacturerSystem.MULTI_MANUFACTURERSYSTEM;
          break;
        case CardKind.GROUP_CARD:
          returnValue = true;
          break;
        case CardKind.QRCODE:
          returnValue =
            manufacturerSystem1 == ManufacturerSystem.BIG ||
            manufacturerSystem1 == ManufacturerSystem.BIG_VIRTUAL;
          break;
        case CardKind.FACE:
          returnValue = manufacturerSystem1 == ManufacturerSystem.DAHUA;
          break;
        case CardKind.MAIL:
          returnValue = manufacturerSystem1 == ManufacturerSystem.MIKROTIK;
          break;
        case CardKind.USER_ACCESS:
          returnValue = true;
          break;
        default:
          break;
      }
    }
    return returnValue;
  }

  /**
   * Returns true if the CardProgrammerKind is compatible with ManufacturerSystem
   * @param cardProgrammerKind
   * @param manufacturerSystem2
   * @returns
   */
  public cardProgrammerManufacturerCompatible(
    kind: CardProgrammerKind,
    manufacturerSystem: ManufacturerSystem
  ) {
    let returnValue: boolean = false;
    if (
      (kind == CardProgrammerKind.EELECTRON &&
        manufacturerSystem == ManufacturerSystem.EELECTRON) ||
      (kind == CardProgrammerKind.GEWISS &&
        manufacturerSystem == ManufacturerSystem.GEWISS) ||
      (kind == CardProgrammerKind.SCHNEIDER &&
        manufacturerSystem == ManufacturerSystem.SCHNEIDER) ||
      (kind == CardProgrammerKind.SIEMENS &&
        manufacturerSystem == ManufacturerSystem.SIEMENS) ||
      (kind == CardProgrammerKind.VIMAR_EELECTRON &&
        manufacturerSystem == ManufacturerSystem.VIMAR_EELECTRON) ||
      (kind == CardProgrammerKind.VIMAR_WELLCONTACT_PLUS &&
        manufacturerSystem == ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS) ||
      (kind == CardProgrammerKind.SALTO_IS &&
        manufacturerSystem == ManufacturerSystem.SALTO_IS) ||
      (kind == CardProgrammerKind.ABB &&
        (manufacturerSystem == ManufacturerSystem.ABB ||
          manufacturerSystem == ManufacturerSystem.ABB_MAC2)) ||
      ((kind == CardProgrammerKind.ABB_MILLENIUM_BUS ||
        kind == CardProgrammerKind.ABB_MILLENIUM_USB) &&
        manufacturerSystem == ManufacturerSystem.ABB_MILLENIUM) ||
      (kind == CardProgrammerKind.ABB_TACTEO &&
        manufacturerSystem == ManufacturerSystem.ABB_TACTEO) ||
      (kind == CardProgrammerKind.DAHUA &&
        manufacturerSystem == ManufacturerSystem.DAHUA_CARD_READER) ||
      (kind == CardProgrammerKind.CISA &&
        manufacturerSystem == ManufacturerSystem.CISA) ||
      (kind == CardProgrammerKind.EELECTRON_MIFARE &&
        manufacturerSystem == ManufacturerSystem.EELECTRON_MIFARE)
    ) {
      returnValue = true;
    }
    return returnValue;
  }

  /**
   * Returns true if the environment is compatible with paper or another environment
   * @param manufacturerSystem1
   * @param manufacturerSystem2
   * @returns
   */
  public manufacturerSystemsCompatible(
    manufacturerSystem1: ManufacturerSystem,
    manufacturerSystem2: ManufacturerSystem
  ): boolean {
    let returnValue: boolean = false;

    if (
      manufacturerSystem1 !== ManufacturerSystem.AMBIGOUS &&
      manufacturerSystem2 !== ManufacturerSystem.AMBIGOUS &&
      manufacturerSystem2 !== ManufacturerSystem.NONE
    ) {
      if (
        manufacturerSystem1 === ManufacturerSystem.ELECTRONE ||
        manufacturerSystem2 === ManufacturerSystem.ELECTRONE
      )
        returnValue = true;
      else if (manufacturerSystem1 === ManufacturerSystem.NONE)
        returnValue = true;
      else if (manufacturerSystem1 === manufacturerSystem2) returnValue = true;
      else {
        if (
          (this._compatibleAccessManufacturerSystem.find(
            (m) => m === manufacturerSystem1
          ) &&
            this._compatibleAccessManufacturerSystem.find(
              (m) => m === manufacturerSystem2
            )) ||
          (this._compatibleAccessManufacturerSystemMifare.find(
            (m) => m === manufacturerSystem1
          ) &&
            this._compatibleAccessManufacturerSystemMifare.find(
              (m) => m === manufacturerSystem2
            )) ||
          (this._compatibleAccessManufacturerSystemKeypad.find(
            (m) => m === manufacturerSystem1
          ) &&
            this._compatibleAccessManufacturerSystemKeypad.find(
              (m) => m === manufacturerSystem2
            )) ||
          (this._compatibleAccessManufacturerSystemCardForKeypad.find(
            (m) => m === manufacturerSystem1
          ) &&
            this._compatibleAccessManufacturerSystemKeypad.find(
              (m) => m === manufacturerSystem2
            ))
        )
          returnValue = true;
        else {
          if (
            this._accessControlManufacturerNoRangeForCardNumber.find(
              (m) => m === manufacturerSystem1
            )
          ) {
            let accessControlManufacturerInPlant: Array<ManufacturerSystem> =
              this._listManufacturerSystem;
            accessControlManufacturerInPlant =
              accessControlManufacturerInPlant.filter(
                (manufacturer) => manufacturer !== manufacturerSystem1
              );
            if (manufacturerSystem1 === ManufacturerSystem.GENERIC_KEYPAD) {
              accessControlManufacturerInPlant =
                accessControlManufacturerInPlant.filter(
                  (manufacturer) =>
                    !this._compatibleAccessManufacturerSystemKeypad.find(
                      (m) => m === manufacturer
                    )
                );
            }

            if (
              accessControlManufacturerInPlant.length === 1 &&
              accessControlManufacturerInPlant[0] === manufacturerSystem2
            ) {
              returnValue = true;
            } else {
              let accessControlManufacturerCompatibleeElectronInPlant: ManufacturerSystem[] =
                accessControlManufacturerInPlant.filter(
                  (m) =>
                    !this._compatibleAccessManufacturerSystem.find(
                      (ms) => ms === m
                    )
                );
              if (
                accessControlManufacturerCompatibleeElectronInPlant.length === 0
              ) {
                returnValue = true;
              }
            }
          }
        }
      }
    }
    return returnValue;
  }

  /**
   * Load access control manufacturer system in plant
   */
  public async loadAccessControlManufacturerSystemInPlant(): Promise<void> {
    this._store
      .select(listEnvironmentAccessControlMaintanance)
      .pipe(
        tap(
          (environmentAccessControlMaintananceObject: GenericStoreApiData) =>
            (this._listManufacturerSystem = [])
        ),
        filter(
          (environmentAccessControlMaintananceObject: GenericStoreApiData) =>
            !!environmentAccessControlMaintananceObject
        ),
        filter(
          (environmentAccessControlMaintananceObject: GenericStoreApiData) =>
            !!environmentAccessControlMaintananceObject
        ),
        filter(
          (environmentAccessControlMaintananceObject: GenericStoreApiData) =>
            environmentAccessControlMaintananceObject.loaded
        ),
        filter(
          (environmentAccessControlMaintananceObject: GenericStoreApiData) =>
            !(
              environmentAccessControlMaintananceObject.errorGeneric ||
              environmentAccessControlMaintananceObject.errorNotYetImplemented
            )
        ),
        map(
          (environmentAccessControlMaintananceObject: GenericStoreApiData) =>
            environmentAccessControlMaintananceObject.data?.data || []
        ),
        map(
          (
            listEnvironmentForAccessMaintanance: Array<EnvironmentAccessControlMaintanance>
          ) => {
            return listEnvironmentForAccessMaintanance
              .map(
                (
                  environmentForAccessMaintanance: EnvironmentAccessControlMaintanance
                ) => environmentForAccessMaintanance.manufacturerSystem
              )
              .filter(
                (
                  manufacturerSystem: ManufacturerSystem,
                  index: number,
                  listManufacturerSystem: Array<ManufacturerSystem>
                ) => {
                  return (
                    index === listManufacturerSystem.indexOf(manufacturerSystem)
                  );
                }
              );
          }
        )
      )
      .subscribe(
        (listAvaiableManufacturerSystem: Array<ManufacturerSystem>) => {
          this._listManufacturerSystem = listAvaiableManufacturerSystem;
        }
      );
  }

  //#endregion

  /**
   * Normalize environment key to build compose object.
   * When environment category is UNDEFINED, it checks environment type, then reassign key as category
   * value.
   *
   * @param environment Environment to check
   * @returns Key formatted object
   */
  private normalizeKeyEnvironmentCategory(environment: Environment): number {
    let keyCategory: number = environment.category;

    if (
      environment.category === EnvironmentCategory.UNDEFINED ||
      !environment.category
    ) {
      switch (environment.environmentType) {
        case EnvironmentType.ROOM:
          keyCategory = EnvironmentCategory.ROOM;
          break;
        case EnvironmentType.COMMON_AREA:
          keyCategory = EnvironmentCategory.COMMON_AREA;
          break;
        case EnvironmentType.CUSTOMIZED:
          keyCategory = EnvironmentCategory.CABINETS;
          break;
        case EnvironmentType.SUITE:
          keyCategory = EnvironmentCategory.SUITE;
          break;
      }
    }

    return keyCategory;
  }

  /**
   *
   * @param idEnvironment
   * @param idUser
   * @returns
   */
  private checkOpenDoorEnvironmentRequestCommandFeedBack(
    idEnvironment: number,
    idUser: number
  ): Promise<AccessResult> {
    const queryString: string = "/checkaccess/" + idEnvironment + "/" + idUser;

    return this._http
      .get<AccessResult>(
        environment.httpServerRoot +
        AppSettings.API_URL_ENVIRONMENT +
        queryString
      )
      .toPromise();
  } //

  /**
   * Generate open request command enviromnet address
   *
   * @param idEnvironment id environment to generate address
   */
  private getEnvironnmentRequestCommandAddress(idEnvironment: number) {
    return (
      AppSettings.INSTANCE_GUID_OMNIA +
      "_" +
      "Environment/" +
      idEnvironment +
      "/RequestOpenDoor"
    );
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectCurrentUserSub?.unsubscribe();
    this._selectedBuildingSubscription?.unsubscribe();
  }
  //#endregion

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        filter(() => this._isUserAuthenticated)
      ).subscribe(() => {
        this.checkAndReloadCompleteEnvironmentList();
        this.checkAndReloadEnvironmentAccessUserOpenableList();
        this.checkAndReloadCompleteEnvironmentDeviceList();
      })
    );
  }

  /**
   * Desubscribe all the events
   */
  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }

  //#endregion

  /**Check if the complete Environment list has already been asked (either successfully or with error) from server at least once before; if  so, reload data*/
  private checkAndReloadCompleteEnvironmentList(): void {
    this._store
      .select(listEnvironment)
      .pipe(
        take(1),
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject.loaded
        )
      )
      .subscribe((listEnvironmentObject: GenericStoreApiData) => {
        this._store.dispatch(environmentLoadInitStartAction());
      });
  }

  /**Check if the complete Environment device list has already been asked (either successfully or with error) from server at least once before; if  so, reload data*/
  private checkAndReloadCompleteEnvironmentDeviceList(): void {
    this._store
      .select(listEnvironmentDevices)
      .pipe(
        take(1),
        filter(
          (listEnvironmentDeviceObject: GenericStoreApiData) =>
            !!listEnvironmentDeviceObject
        ),
        filter(
          (listEnvironmentDeviceObject: GenericStoreApiData) =>
            !!listEnvironmentDeviceObject
        ),
        filter(
          (listEnvironmentDeviceObject: GenericStoreApiData) =>
            listEnvironmentDeviceObject.loaded
        )
      )
      .subscribe((listEnvironmentObject: GenericStoreApiData) => {
        this._store.dispatch(environmentDeviceLoadInitStartAction());
      });
  }

  /**Check if the list of the user-openable Environments  has already been asked (either successfully or with error) from server at least once before; if  so, reload data*/
  private checkAndReloadEnvironmentAccessUserOpenableList(): void {
    this._store
      .select(listEnvironmentAccessUserOpenable)
      .pipe(
        take(1),
        filter(
          (listEnvironmentAccessUserOpenableObject: GenericStoreApiData) =>
            !!listEnvironmentAccessUserOpenableObject
        ),
        filter(
          (listEnvironmentAccessUserOpenableObject: GenericStoreApiData) =>
            !!listEnvironmentAccessUserOpenableObject
        ),
        filter(
          (listEnvironmentAccessUserOpenableObject: GenericStoreApiData) =>
            listEnvironmentAccessUserOpenableObject.loaded
        )
      )
      .subscribe(
        (listEnvironmentAccessUserOpenableObject: GenericStoreApiData) => {
          this._store.dispatch(
            environmentAccessUserOpenableLoadListFromApiAction()
          );
        }
      );
  }

  /**
   * Load current user data
   */
  private loadCurrentUser(): void {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe((currentUser: CurrentSessionData) => {
        this._currentUser = currentUser;

        if (currentUser) {
          this._isUserAuthenticated = !!currentUser?.accessToken;
        } else {
          this._isUserAuthenticated = false;
        }
      });
  }

  //#region EnvironmentDevice

  /**
   * Get the list of the environemnt and the devices (optionally of a specific gateway)
   * @returns
   */
  public getEnvironmentWithDeviceRelations(
    instanceguid?: string
  ): Observable<GenericApiData> {
    let queryString = "";
    if (instanceguid && instanceguid != null)
      queryString = "?instanceguid=" + instanceguid;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_ENVIRONMENT +
        "/tree" +
        queryString
      )
      .pipe(
        map((returnEnvironmentStore: GenericApiData) => {
          if (returnEnvironmentStore) {
            return returnEnvironmentStore;
          }
        })
      );
  }

  /**
   * Get the list of the environemnt and the devices (optionally of a specific gateway) from the last modify of the store
   * @returns
   */
  public getEnvironmentWithDeviceRelationsByLastModify(
    environmentStore: GenericApiData,
    instanceguid?: string
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string =
      environmentStore?.lastAPIModified || "";

    let queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    if (instanceguid && instanceguid != null)
      queryString = "&instanceguid=" + instanceguid;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_ENVIRONMENT +
        "/tree" +
        queryString
      )
      .pipe(
        map((returnEnvironmentStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnEnvironmentStore) {
            environmentStore = returnEnvironmentStore;
          }
          return environmentStore;
        })
      );
  }

  /**
   * Update params of environment
   * @param environmentUpdate
   */
  public updateEnvironment(environmentUpdate: Environment) {
    return this._http
      .put<Environment>(
        environment.httpServerRoot + AppSettings.API_URL_ENVIRONMENT_CONFIG,
        environmentUpdate
      )
      .pipe<Environment>(
        tap(() => {
          this.checkAndReloadCompleteEnvironmentList();
        })
      );
  }

  /**
   * Delete environment
   * @param idEnvironment
   * @returns
   */
  public deleteEnvironment(idEnvironment: number) {
    return this._http
      .delete<Environment>(
        environment.httpServerRoot +
        AppSettings.API_URL_ENVIRONMENT +
        "/" +
        idEnvironment
      )
      .subscribe(
        () => {
          //nothing to do if it's ok
        },
        (error) => {
          this.handleApiResponseError();
        }
      );
  }

  /**
   * Delete environment
   * @param idEnvironment
   * @returns
   */
  public deleteEnvironmentPromise(idEnvironment: number) {
    return this._http.delete<Environment>(
      environment.httpServerRoot +
      AppSettings.API_URL_ENVIRONMENT +
      "/" +
      idEnvironment
    );
  }

  public getDescriptionCategory(category: EnvironmentCategory): string {
    let lblCategory: string = "";
    switch (category) {
      case EnvironmentCategory.APARTAMENT:
        lblCategory = this._lblNewApartament;
        break;
      case EnvironmentCategory.COMMON_AREA:
        lblCategory = this._lblNewCommonArea;
        break;
      case EnvironmentCategory.ELAVATOR_FLOOR:
        lblCategory = this._lblNewElevatorFloor;
        break;
      case EnvironmentCategory.EXTERNAL_ACCESS:
        lblCategory = this._lblNewExternalAccess;
        break;
      case EnvironmentCategory.FLOOR:
        lblCategory = this._lblNewFloor;
        break;
      case EnvironmentCategory.GARAGE:
        lblCategory = this._lblNewGarage;
        break;
    }
    return lblCategory;
  }

  /**
   * Insert environment
   * @param idEnvironment
   * @returns
   */
  public insertEnvironment(
    instanceGUID: string,
    idArea: number,
    category?: EnvironmentCategory,
    idParent?: number,
    sub?: string,
    unit?: string,
  ): Observable<Environment> {
    let body = {};
    body = {
      environmentDescription: this.getDescriptionCategory(category),
      customDescription: this.getDescriptionCategory(category),
      nRoom: this.getDescriptionCategory(category),
      category: category,
      idParent: idParent,
      sub: sub,
      unit: unit,
    };
    body["idArea"] = +idArea;
    body["instanceGUID"] = instanceGUID;

    return this._http.post<Environment>(
      environment.httpServerRoot + AppSettings.API_URL_ENVIRONMENT,
      body
    );
  }

  /**
   *
   * Insert condo environment
   * @param idEnvironment
   * @returns
   * @param instanceGUID
   * @param idArea
   * @param category
   * @param name
   * @param removable set if the environment is removable
   * @returns
   */
  public insertCondoEnvironment(
    instanceGUID: string,
    idArea: number,
    category: EnvironmentCategory,
    name: string,
    removable: boolean = true
  ): Observable<Environment> {
    let body = {};
    body = {
      environmentDescription: name,
      customDescription: name,
      category: category,
      removable: removable,
    };

    body["idArea"] = +idArea;
    body["instanceGUID"] = instanceGUID;

    return this._http.post<Environment>(
      environment.httpServerRoot + AppSettings.API_URL_ENVIRONMENT,
      body
    );
  }

  /**
   * Set the open door command
   * @param idEnvironment
   * @param instanceGuid
   * @param address
   * @param value
   * @returns
   */
  public async setOpenDoorCommand(idEnvironment, instanceGuid, address) {
    let body = {};
    body["instanceGuid"] = instanceGuid;
    body["address"] = address;
    body["value"] = 1;
    body["delay"] = 0;
    return await this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_ENVIRONMENT +
        "/" +
        idEnvironment +
        "/opendoor",
        body
      )
      .toPromise();
  }

  /**
   * Insert environment - device relations
   * @param idEnvironment
   * @returns
   */
  public insertEnvironmentDevice(idEnvironment: number, idDevice: number) {
    let body = {};
    body["idDevice"] = idDevice;
    body["idEnvironment"] = idEnvironment;

    return this._http.put(
      environment.httpServerRoot + AppSettings.API_URL_ENVIRONMENT_DEVICE,
      body
    );
  }

  /**
   * Remove environment - device relations
   * @param idEnvironment
   * @returns
   */
  public removeEnvironmentDevice(idEnvironment: number, idDevice: number) {
    const queryString: string =
      "?idenvironment=" + idEnvironment + "&iddevice=" + idDevice;

    return this._http.delete(
      environment.httpServerRoot +
      AppSettings.API_URL_ENVIRONMENT_DEVICE +
      queryString
    );
  }

  /**
   * Get the icon for environment type
   * @param environment
   * @returns
   */
  public getIconByEnvironmentAccessControlMaintanance(
    environment: EnvironmentAccessControlMaintanance
  ): string {
    switch (environment.environmentType) {
      case EnvironmentType.ROOM:
        return "bed";
        break;
      case EnvironmentType.SUITE:
        return "star";
        break;
      case EnvironmentType.CISA_ZONE:
      case EnvironmentType.COMMON_AREA:
        return "cube";
        break;
      case EnvironmentType.CUSTOMIZED:
        return "grid";
        break;
      case EnvironmentType.CISA_CATEGORY:
        return "grid";
        break;
    }
  }

  /**
   * Show error message if something went wrong saving gateway settings or taking them
   */
  private async handleApiResponseError(): Promise<void> {
    this._alertService.presentAlertSomethingWrong();
  }
  //#endregion

  /**
   * Show popup to select category when adding a building part
   * @param envCategory category of environment in which the building part is added. When the building part is not added to a specific environment is null.
   * @returns promise that resolve when the popup is closed
   */
  public async openPopupCategory(
    envCategory: EnvironmentCategory = null
  ): Promise<any> {
    let listButton: Array<ItemEnvironmentCategory> =
      new Array<ItemEnvironmentCategory>();
    let listEnvironmentCategory: Array<EnvironmentCategoryReverse>;

    listEnvironmentCategory = Object.values(EnvironmentCategory)
      .filter((category: any) => !isNaN(category.toString()))
      .map(function (val: number): EnvironmentCategoryReverse {
        const envObject = {
          key: val,
          value: EnvironmentCategory[val],
        };
        return envObject;
      });

    listEnvironmentCategory = listEnvironmentCategory.filter(
      (listElement: EnvironmentCategoryReverse) => {
        return (
          listElement.key === EnvironmentCategory.COMMON_AREA ||
          listElement.key === EnvironmentCategory.FLOOR ||
          listElement.key === EnvironmentCategory.ELAVATOR_FLOOR ||
          listElement.key === EnvironmentCategory.GARAGE ||
          listElement.key === EnvironmentCategory.APARTAMENT ||
          listElement.key === EnvironmentCategory.EXTERNAL_ACCESS ||
          listElement.key === EnvironmentCategory.STAIRS ||
          listElement.key === EnvironmentCategory.CONDOMINIUM ||
          listElement.key === EnvironmentCategory.ROOM
        );
      }
    );

    //If open popup in a children enviroment filter another time EnvironmentCategoryReverse
    if (envCategory) {
      listEnvironmentCategory = listEnvironmentCategory.filter(
        (listElement: EnvironmentCategoryReverse) =>
          this.addEnvironmentConstraints(listElement, envCategory)
      );
    }

    for (let category of listEnvironmentCategory) {
      listButton.push({
        text: this.convertEnumToString(category.key),
        icon: this.getIconCategory(category.key),
        id: category.key,
      });
    }

    let modalConfiguration = {
      component: PopupEnvironmentCategoryComponent,
      cssClass: "category-picker-modal",
      componentProps: {
        header: this._lblSelectBuildingPartsToCreate,
        categories: listButton,
      },
    };

    let modal = await this._modalController.create(modalConfiguration);

    await modal.present();

    await this._loaderService.dismissLoaderWaiting();

    let result = await modal.onDidDismiss();

    return result;
  }

  /**
   * Function for filtering a list of category with constraints
   * @param listElement element of category list
   * @param envCategory category of selected environment
   * @returns
   */
  public addEnvironmentConstraints(
    listElement: EnvironmentCategoryReverse,
    envCategory: EnvironmentCategory
  ): boolean {
    //A common area cannot host any sub-environment
    if (envCategory === EnvironmentCategory.COMMON_AREA) {
      return false;
    }

    //Exclude category if it's equal to itself
    if (listElement.key === envCategory) {
      return false;
    }

    if (
      envCategory === EnvironmentCategory.APARTAMENT &&
      listElement.key !== EnvironmentCategory.ROOM
    ) {
      return false;
    }

    if (envCategory !== EnvironmentCategory.CONDOMINIUM) {
      if (envCategory === EnvironmentCategory.APARTAMENT) {
        return (
          listElement.key !== EnvironmentCategory.COMMON_AREA &&
          listElement.key !== EnvironmentCategory.EXTERNAL_ACCESS &&
          listElement.key !== EnvironmentCategory.CONDOMINIUM
        );
      }

      if (envCategory === EnvironmentCategory.GARAGE) {
        return (
          listElement.key !== EnvironmentCategory.APARTAMENT &&
          listElement.key !== EnvironmentCategory.CONDOMINIUM
        );
      }

      return true;
    }

    return true;
  }

  /**
   * Added permission to environment
   * @param idEnvironment
   */
  public async addedPermission(
    session: CurrentSessionData,
    idEnvironment: number
  ): Promise<void> {
    this._permissionService.handlePermissionUserEnvironment(
      idEnvironment,
      session.idUserRemote,
      true
    );
  }

  /**
   * Build tree to show parents and children of environments
   * @param environmentList list of environment
   * @returns list of parent environments with children environments
   */
  public buildTree(environmentList: Environment[]): EnvironmentTree[] {
    const map = new Map();
    const environmentTreeList = [];

    //Add children property to each env and add it to the map
    for (const env of environmentList as EnvironmentTree[]) {
      env.children = [];
      map.set(env.id, env);
    }

    //If env is a parent add it to environmentTreeList, else add env to property "children" of a parent
    for (const env of environmentList as EnvironmentTree[]) {
      if (!map.get(env.idParent)) {
        environmentTreeList.push(env);
      } else {
        const parentEnv: EnvironmentTree = map.get(env.idParent);
        if (parentEnv) {
          parentEnv.children.push(env);
        }
      }
    }
    return environmentTreeList;
  }

  /**
   * Convert enum to string for environment category
   * @param value
   * @returns
   */
  private convertEnumToString(value: EnvironmentCategory): string {
    return EnumStringConverter.environmentCategoryToStringEnum(
      value,
      this._translateService,
      false
    );
  }

  /**
   * Get icon by category
   * @param key
   * @returns
   */
  public getIconCategory(key: EnvironmentCategory): string {
    let cssSegmentIconClass: string = "";

    switch (key) {
      case EnvironmentCategory.ROOM:
      case EnvironmentCategory.SUITE:
      case EnvironmentCategory.JUNIOR_SUITE:
      case EnvironmentCategory.SINGLE_ROOM:
      case EnvironmentCategory.DOUBLE_ROOM:
      case EnvironmentCategory.TRIPLE_ROOM:
      case EnvironmentCategory.FAMILY_ROOM:
      case EnvironmentCategory.DORMITORY:
        cssSegmentIconClass = "assets/icon/ico-room.svg";
        break;
      case EnvironmentCategory.APARTAMENT:
        cssSegmentIconClass = "assets/icon/ico-apartment.svg";
        break;
      case EnvironmentCategory.COMMON_AREA:
        cssSegmentIconClass = "assets/icon/ico-common-area.svg";
        break;
      case EnvironmentCategory.FLOOR:
        cssSegmentIconClass = "assets/icon/ico-floor.svg";
        break;
      case EnvironmentCategory.ELAVATOR_FLOOR:
        cssSegmentIconClass = "assets/icon/ico-elevator-floor.svg";
        break;
      case EnvironmentCategory.GARAGE:
        cssSegmentIconClass = "car";
        break;
      case EnvironmentCategory.EXTERNAL_ACCESS:
        cssSegmentIconClass = "enter-outline";
        break;
      case EnvironmentCategory.CONDOMINIUM:
        cssSegmentIconClass = "business-outline";
        break;
      case EnvironmentCategory.STAIRS:
        cssSegmentIconClass = "assets/icon/ico-stairs.svg";
        break;
      default:
        cssSegmentIconClass = "cube";
    }

    return cssSegmentIconClass;
  }

  /**
   * Check if an environment is expanded
   * @param id environment id
   * @returns true if expanded
   */
  public isExpanded(id: number): boolean {
    return this._expandedEnvironments.has(id);
  }

  /**
   * Set expansion state of an environment
   * @param id environment id
   * @param expanded expansion state
   */
  public setExpanded(id: number, expanded: boolean): void {
    if (expanded) {
      this._expandedEnvironments.add(id);
    } else {
      this._expandedEnvironments.delete(id);
    }
  }

  /**
   * Subscribe to building change to reset expansion state
   */
  private subscribeBuildingChange(): void {
    this._selectedBuildingSubscription = this._store
      .select(selectedBuilding)
      .subscribe((building: Building) => {
        if (building && building.id !== this._currentBuildingId) {
          this._currentBuildingId = building.id;
          this.clearExpandedEnvironments();
        }
      });
  }

  /**
   * Clear expansion state
   */
  public clearExpandedEnvironments(): void {
    this._expandedEnvironments.clear();
  }
}
